import React, { createContext, useCallback, useMemo, useContext, useState } from 'react';
import { getDefaultDates } from '@utils/dates';
import {
  useGetCampaignFolders,
  useFinancialMetrics,
  usePerformanceMetrics,
  useGetTradePartners,
} from '@utils/hooks/Campaign';
import { useLocalStorage } from '@utils/hooks/Common';
import { useTradeTenants } from '@utils/hooks/TradeAds';
import { useSelector } from 'react-redux';

const INITIAL_CONTEXT = {
  campaigns: {
    campaigns: [],
    fetchMoreData: () => {},
    hasNextPage: false,
    isLoading: false,
    lastCampaign: null,
  },
  performanceMetrics: {
    isLoading: false,
    financialMetrics: [],
    defaultDate: null,
  },
};

export const CampaignsContext = createContext(INITIAL_CONTEXT);

export const CampaignsProvider = ({ children }) => {
  const defaultDate = getDefaultDates();

  const genericReducer = useSelector((state) => state.genericReducer);

  const [filters, setFilters] = useLocalStorage('filter_incentive_campaign', {
    incentive_campaign_status: [],
    incentive_campaign_name: '',
    appliedFilters: false,
  });

  const [nameFilter, setNameFilter] = useState('');

  const periodParams = genericReducer?.period || '';

  const { performanceMetrics, isLoading: isLoadingPerformanceMetrics } = usePerformanceMetrics(
    filters,
    defaultDate,
    periodParams
  );

  const { financialMetrics, isLoading: isLoadingFinancialMetrics } = useFinancialMetrics(
    filters,
    defaultDate,
    periodParams
  );

  const tenantsData = useTradeTenants();
  const currentGroupID = window.location.hash.substring(2).split('/')[0];

  const { campaigns, fetchMoreData, hasNextPage, isLoading, error, lastCampaign, mutate: mutateCampaigns } = useGetCampaignFolders(
    { ...filters, incentive_campaign_name: nameFilter },
    defaultDate,
    currentGroupID,
    periodParams
  );

  const tradePartnersResponse = useGetTradePartners();

  const hasTradeAds = campaigns.filter(
    (media) => media.campaign_tags && media.campaign_tags.length > 0
  );
  const hasOnlyCampaignTradeAds = hasTradeAds && hasTradeAds.length === campaigns.length;

  const [isCreatingCampaign, setIsCreatingCampaign] = useLocalStorage('new_campaign');

  const tradePartners = useMemo(() => {
    const hasTradePartners = tradePartnersResponse?.data?.length;
    if (hasTradePartners !== tradePartners?.length && hasTradePartners) {
      return tradePartnersResponse.data.sort((a, b) => a.value.localeCompare(b.value));
    }
  }, [tradePartnersResponse]);

  const shouldResetFilters =
    (filters.appliedFilters || nameFilter) &&
    (!campaigns || campaigns.length === 0 || hasOnlyCampaignTradeAds) &&
    isLoading === undefined;

  const resetFilters = () => {
   setTimeout(() => {
      setNameFilter('');
      setFilters({
        incentive_campaign_status: [],
        objective: [],
        appliedFilters: false,
      });
    }, 1000);
  };

  const disableFilters = () => setFilters({ ...filters, appliedFilters: false });

  const setNewFilters = useCallback(
    (params, appliedFilters = false) => {
      setFilters({ ...params, appliedFilters });
    },
    [setFilters]
  );

  const getTagPartner = useMemo(() => {
    const arrayTenants = tenantsData?.tenants?.map((tenant) => {
      return { value: tenant.exchanged_tags[0], label: tenant.target_tenant_name };
    });
    return arrayTenants;
  }, [tenantsData]);
  const providerStructure = {
    campaigns: {
      campaigns,
      fetchMoreData,
      hasNextPage,
      isLoading,
      error,
      lastCampaign,
      mutateCampaigns
    },
    performanceMetrics: {
      isLoading: isLoadingPerformanceMetrics,
      performanceMetrics,
      defaultDate,
      error,
    },
    financialMetrics: {
      isLoading: isLoadingFinancialMetrics,
      financialMetrics,
      defaultDate,
      error,
    },
  };

  return (
    <CampaignsContext.Provider
      value={{
        ...providerStructure,
        filters,
        setNewFilters,
        setNameFilter,
        disableFilters,
        resetFilters,
        shouldResetFilters,
        getTagPartner,
        isCreatingCampaign,
        setIsCreatingCampaign,
        tradePartnersResponse,
        tradePartners,
      }}
    >
      {children}
    </CampaignsContext.Provider>
  );
};

export const useCampaign = () => {
  const context = useContext(CampaignsContext);

  if (!context) throw new Error('Expected to be wrapped in a CampaignFormStep');

  return context;
};
